/**
 * Push notifications helper.
 * Deux canaux :
 *  - Web Push VAPID (PWA / navigateur)
 *  - FCM natif via @capacitor/push-notifications (app Capacitor Android/iOS)
 * - registerAndSubscribe() : prompts permission, subscribes, saves on server.
 * - unsubscribe() : removes from server & pushManager.
 * - getSubscriptionState() : 'unsupported' | 'blocked' | 'default' | 'subscribed' | 'not-subscribed'
 */
import { Capacitor } from '@capacitor/core';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FCM_TOKEN_KEY = 'mibeko_fcm_token';

export const isNativeApp = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

const urlB64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const isPushSupported = () =>
  isNativeApp() ||
  (typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window);

export const getSubscriptionState = async () => {
  if (isNativeApp()) {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const perm = await PushNotifications.checkPermissions();
      if (perm.receive === 'denied') return 'blocked';
      if (localStorage.getItem(FCM_TOKEN_KEY)) return 'subscribed';
      return perm.receive === 'granted' ? 'not-subscribed' : 'default';
    } catch {
      return 'default';
    }
  }
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) return 'subscribed';
    return Notification.permission === 'granted' ? 'not-subscribed' : 'default';
  } catch {
    return 'default';
  }
};

const registerNative = async () => {
  const { PushNotifications } = await import('@capacitor/push-notifications');
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive !== 'granted') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') throw new Error('permission-denied');

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('registration-timeout')), 15000);
    PushNotifications.addListener('registration', async ({ value }) => {
      clearTimeout(timer);
      try {
        localStorage.setItem(FCM_TOKEN_KEY, value);
        await fetch(`${API_URL}/api/push/subscribe-fcm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: value,
            platform: Capacitor.getPlatform(),
            userAgent: navigator.userAgent,
          }),
        });
        resolve(value);
      } catch (err) {
        reject(err);
      }
    });
    PushNotifications.addListener('registrationError', () => {
      clearTimeout(timer);
      reject(new Error('registration-failed'));
    });
    PushNotifications.register();
  });
};

// A appeler une fois au demarrage de l'app : gere le clic sur une notification native
export const initNativePushListeners = async () => {
  if (!isNativeApp()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const url = action?.notification?.data?.url;
      if (url) window.location.href = url;
    });
  } catch {
    // plugin absent (build web) : ignore
  }
};

// Ouvre l'ecran de reglages de notifications de l'app (natif uniquement).
// Permet a l'utilisateur de reautoriser les notifications apres un refus.
export const openAppNotificationSettings = async () => {
  if (!isNativeApp()) return false;
  try {
    const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
    if (Capacitor.getPlatform() === 'ios') {
      await NativeSettings.openIOS({ option: IOSSettings.App });
    } else {
      await NativeSettings.openAndroid({ option: AndroidSettings.AppNotification });
    }
    return true;
  } catch (err) {
    console.info('[Mibeko] openAppNotificationSettings failed:', err?.message || err);
    return false;
  }
};

export const registerAndSubscribe = async () => {
  if (isNativeApp()) return registerNative();
  if (!isPushSupported()) throw new Error('unsupported');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('permission-denied');

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...existing.toJSON(), userAgent: navigator.userAgent }),
    });
    return existing;
  }

  const { publicKey } = await (await fetch(`${API_URL}/api/push/vapid-public-key`)).json();
  if (!publicKey) throw new Error('no-vapid-key');

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlB64ToUint8Array(publicKey),
  });

  await fetch(`${API_URL}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...sub.toJSON(), userAgent: navigator.userAgent }),
  });
  return sub;
};

export const unsubscribe = async () => {
  if (isNativeApp()) {
    const token = localStorage.getItem(FCM_TOKEN_KEY);
    if (token) {
      await fetch(`${API_URL}/api/push/unsubscribe-fcm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      localStorage.removeItem(FCM_TOKEN_KEY);
    }
    return;
  }
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await fetch(`${API_URL}/api/push/unsubscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint: sub.endpoint }),
  });
  await sub.unsubscribe();
};
